import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { X, Plus } from 'lucide-react';
import api from '@/lib/axios';
import { inputLocalAIso } from '@/lib/tareas';
import { useCrearTarea, useTableros } from '@/hooks/useTareas';
import { PRIORIDADES, type PrioridadTarea, type TareaDetalle } from '@/types/tareas';
import { AsignadosPicker, EtiquetasPicker } from '@/components/tareas/Pickers';
import { RelacionesPicker, type RelacionSeleccion } from '@/components/tareas/RelacionesPicker';
import { RelacionChip } from '@/components/tareas/TareaPiezas';

const campo = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableroId?: string;
  columnaId?: string;
  parentId?: string;
  relacionesIniciales?: RelacionSeleccion[];
  asignadosIniciales?: string[];
  onCreated?: (tarea: TareaDetalle) => void;
}

export function TareaFormDialog({
  open, onOpenChange, tableroId, columnaId, parentId, relacionesIniciales, asignadosIniciales, onCreated,
}: Props) {
  const crear = useCrearTarea();
  const { data: tablerosData } = useTableros(open);

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadTarea>('media');
  const [vencimiento, setVencimiento] = useState('');
  const [asignadoIds, setAsignadoIds] = useState<string[]>([]);
  const [etiquetaIds, setEtiquetaIds] = useState<string[]>([]);
  const [relaciones, setRelaciones] = useState<(RelacionSeleccion & { id: string })[]>([]);
  const [tableroElegido, setTableroElegido] = useState('');
  const [consultorioId, setConsultorioId] = useState('');

  const { data: consultorios = [] } = useQuery<any[]>({
    queryKey: ['consultorios'],
    queryFn: () => api.get('/api/clinica/consultorios').then(r => r.data),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Claves serializadas: los llamadores suelen pasar arrays nuevos en cada render y no deben reiniciar el formulario.
  const relacionesKey = JSON.stringify(relacionesIniciales ?? []);
  const asignadosKey = JSON.stringify(asignadosIniciales ?? []);

  useEffect(() => {
    if (!open) return;
    setTitulo('');
    setDescripcion('');
    setPrioridad('media');
    setVencimiento('');
    setAsignadoIds(JSON.parse(asignadosKey));
    setEtiquetaIds([]);
    setConsultorioId('');
    setRelaciones((JSON.parse(relacionesKey) as RelacionSeleccion[]).map(r => ({ ...r, id: `${r.entidadTipo}:${r.entidadId}` })));
  }, [open, relacionesKey, asignadosKey]);

  useEffect(() => {
    if (open) setTableroElegido(tableroId ?? tablerosData?.defaultId ?? '');
  }, [open, tableroId, tablerosData?.defaultId]);

  const agregarRelacion = (r: RelacionSeleccion) =>
    setRelaciones(prev => (prev.some(p => p.id === `${r.entidadTipo}:${r.entidadId}`)
      ? prev
      : [...prev, { ...r, id: `${r.entidadTipo}:${r.entidadId}` }]));

  const puedeGuardar = titulo.trim().length > 0 && !!tableroElegido && !crear.isPending;

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar) return;
    crear.mutate({
      titulo: titulo.trim(),
      tableroId: tableroElegido,
      columnaId: tableroElegido === tableroId ? columnaId : undefined,
      parentId,
      descripcion: descripcion.trim() || undefined,
      prioridad,
      fechaVencimiento: inputLocalAIso(vencimiento) ?? undefined,
      consultorioId: consultorioId || undefined,
      asignadoIds,
      etiquetaIds,
      relaciones: relaciones.map(r => ({ entidadTipo: r.entidadTipo, entidadId: r.entidadId })),
    }, {
      onSuccess: (tarea) => {
        onOpenChange(false);
        onCreated?.(tarea);
      },
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-[calc(100vw-2rem)] max-w-xl max-h-[94vh] overflow-y-auto focus:outline-none">
          <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-6 py-4 rounded-t-2xl z-10">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              {parentId ? 'Nueva subtarea' : 'Nueva tarea'}
            </Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Cerrar">
              <X size={18} />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">Formulario para crear una tarea</Dialog.Description>

          <form onSubmit={guardar} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                maxLength={255}
                placeholder="Ej. Llamar para confirmar tratamiento"
                className={campo}
              />
              <p className="mt-1 text-[11px] text-text-secondary">
                El título no se cifra: no escribas datos clínicos del paciente aquí, usa la descripción.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Descripción</label>
              <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={3} className={campo} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Prioridad</label>
                <select value={prioridad} onChange={e => setPrioridad(e.target.value as PrioridadTarea)} className={campo}>
                  {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Vencimiento</label>
                <input type="datetime-local" value={vencimiento} onChange={e => setVencimiento(e.target.value)} className={campo} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Tablero</label>
                <select value={tableroElegido} onChange={e => setTableroElegido(e.target.value)} className={campo}>
                  {(tablerosData?.tableros ?? []).map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Consultorio</label>
                <select value={consultorioId} onChange={e => setConsultorioId(e.target.value)} className={campo}>
                  <option value="">Predeterminado</option>
                  {consultorios.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Asignados</label>
              <AsignadosPicker value={asignadoIds} onChange={setAsignadoIds} />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Etiquetas</label>
              <EtiquetasPicker value={etiquetaIds} onChange={setEtiquetaIds} />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Relacionada con</label>
              <div className="flex flex-wrap items-center gap-2">
                {relaciones.map(r => (
                  <RelacionChip
                    key={r.id}
                    relacion={{ id: r.id, tipo: r.entidadTipo, entidadId: null, pacienteId: null, citaId: null, label: r.label, fecha: null, oculto: false }}
                    onQuitar={() => setRelaciones(prev => prev.filter(p => p.id !== r.id))}
                  />
                ))}
                <RelacionesPicker onAgregar={agregarRelacion} fechaReferencia={inputLocalAIso(vencimiento)}>
                  <button type="button" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed border-gray-300 text-xs text-text-secondary hover:border-primary-300 hover:text-primary-600">
                    <Plus size={12} /> Paciente, consulta, cita o presupuesto
                  </button>
                </RelacionesPicker>
              </div>
              <p className="mt-1 text-[11px] text-text-secondary">Al ligar una consulta, cita o presupuesto también se liga su paciente.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50">
                Cancelar
              </Dialog.Close>
              <button
                type="submit"
                disabled={!puedeGuardar}
                className="px-4 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {crear.isPending ? 'Creando…' : 'Crear tarea'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
